import {
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { GraphsService } from '@service/graphs/graphs.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelType, getAnalogTypesSet } from 'icreate/common/models/channel-type';
import {
    Graph,
    BarGraph,
    BarGraphChannel,
    AxisMeasuringRange,
    MAX_BAR_GRAPH_CHANNELS,
} from 'icreate/common/models/graphs';
import _ from 'lodash';
import { Observable, Subscription, lastValueFrom, take } from 'rxjs';

@Component({
    selector: 'app-bar-graph-table',
    templateUrl: './bar-graph-table.component.html',
    styleUrl: './bar-graph-table.component.css',
    standalone: false,
})
export class BarGraphTableComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
    readonly maxChannelSlots = MAX_BAR_GRAPH_CHANNELS;
    readonly axisMeasuringRangeOptions = [
        {
            value: AxisMeasuringRange.Fixed,
            translate: 'graph.bar.table.axis-setting.range-format.fixed-value',
        },
        {
            value: AxisMeasuringRange.Relative,
            translate: 'graph.bar.table.axis-setting.range-format.relative',
        },
    ];

    readonly axisMeasuringReferenceOptions: SimpleDropdownOption[] = [
        { value: 3, string: '3' },
        { value: 4, string: '4' },
        { value: 5, string: '5' },
        { value: 6, string: '6' },
    ];

    @Input() graph: Graph;
    @Input() closeEvent: Observable<void>;
    @Input() saveEvent: Observable<void>;

    @Output() onUpdateSave: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() onCloseGraph: EventEmitter<void> = new EventEmitter<void>();
    @Output() saveCompleted = new EventEmitter<void>();

    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    @ViewChild('rangeDropdownButton') rangeDropdownButton!: ElementRef;
    @ViewChild('rangeDropdownMenu') rangeDropdownMenu!: ElementRef;
    @ViewChild('referenceDropdownButton') referenceDropdownButton!: ElementRef;
    @ViewChild('referenceDropdownMenu') referenceDropdownMenu!: ElementRef;

    barGraph: BarGraph;
    initialGraph: BarGraph;
    allowedTypesSet: Set<ChannelType> = getAnalogTypesSet();

    saveIsEnabled: boolean = false;

    closeSubscription: Subscription;
    saveSubscription: Subscription;

    isRangeDropdownOpen: boolean = false;
    selectedRange: AxisMeasuringRange;
    isReferenceDropdownOpen: boolean = false;
    hoveredOption: AxisMeasuringRange | null = null;

    openRowIndex: number;
    selectedRowIndex: number;

    constructor(
        private graphsService: GraphsService,
        private translate: TranslateService,
        private dialog: MatDialog
    ) {}

    @HostListener('document:click', ['$event'])
    clickOutside(event: any) {
        if (
            this.isRangeDropdownOpen &&
            !this.rangeDropdownMenu?.nativeElement.contains(event.target) &&
            !this.rangeDropdownButton?.nativeElement.contains(event.target)
        ) {
            this.isRangeDropdownOpen = false;
        }

        if (
            this.isReferenceDropdownOpen &&
            !this.referenceDropdownMenu?.nativeElement.contains(event.target) &&
            !this.referenceDropdownButton?.nativeElement.contains(event.target)
        ) {
            this.isReferenceDropdownOpen = false;
        }
    }

    async ngOnInit() {
        await this.loadGraph();
        this.closeSubscription = this.closeEvent.subscribe(async () => await this.cancel());
        this.saveSubscription = this.saveEvent.subscribe(async () => await this.save());
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (this.graph && changes.graph && !changes.graph.firstChange) {
            await this.loadGraph();
        }
    }

    ngDoCheck(): void {
        const isEqual = _.isEqual(this.initialGraph, this.barGraph);
        this.onUpdateSave.emit(!isEqual);
    }

    ngOnDestroy(): void {
        this.closeSubscription?.unsubscribe();
        this.saveSubscription?.unsubscribe();
    }

    async loadGraph() {
        this.graphsService.showLoadingSpinner(true);
        this.barGraph = await this.graphsService.getGraphById(this.graph.id, this.graph.type);
        if (!this.barGraph) {
            this.barGraph = new BarGraph();
            this.barGraph.graphId = this.graph.id;
            this.barGraph.channels = [];
        } else if (this.barGraph.channels) {
            this.barGraph.channels = this.barGraph.channels?.length
                ? this.barGraph.channels
                      .sort((a: any, b: any) => a.rowNumber - b.rowNumber)
                      .map(({ channel, barLabel }) => ({ channel, barLabel }))
                : [];
        } else {
            this.barGraph.channels = [];
        }
        this.initialGraph = _.cloneDeep(this.barGraph);
        this.graphsService.showLoadingSpinner(false);
    }

    showAddNewChannel() {
        return !this.barGraph.channels || this.barGraph.channels.length < this.maxChannelSlots;
    }

    async addChannelRow() {
        const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
            data: {
                vesselId: this.graph.vesselId,
                assignedChannel: undefined,
                allowedTypesSet: this.allowedTypesSet,
                setShouldDisableTypes: true,
            },
        });
        const channelSelected: Channel = await lastValueFrom(dialogRef.afterClosed());
        if (channelSelected) {
            const newBarGraphChannel: BarGraphChannel = {
                channel: channelSelected,
                barLabel: undefined,
            };
            this.barGraph.channels.push(newBarGraphChannel);
        }
    }

    deleteChannelRow(index) {
        this.barGraph.channels.splice(index, 1);
    }

    channelRowUpdated(channel, index) {
        this.barGraph.channels[index] = channel;
    }

    async cancel() {
        if (!_.isEqual(this.initialGraph, this.barGraph)) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.save();
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            }
            this.onCloseGraph.emit();
        } else {
            this.onCloseGraph.emit();
        }
    }

    async save() {
        if (_.isEqual(this.initialGraph, this.barGraph)) return;
        try {
            this.graphsService.showLoadingSpinner(true);
            await this.graphsService.updateBarGraph(this.barGraph);
            this.initialGraph = _.cloneDeep(this.barGraph);
            this.onUpdateSave.emit(false);
            this.graphsService.showLoadingSpinner(false);
            this.saveCompleted.emit();
        } catch (err) {
            console.error(err);
            this.graphsService.showLoadingSpinner(false);
        }
    }

    toggleRangeDropdown() {
        this.isRangeDropdownOpen = !this.isRangeDropdownOpen;
    }

    onSelectRange(value: AxisMeasuringRange) {
        this.barGraph.displayType = value;
        this.isRangeDropdownOpen = false;
    }

    getSelectedRangeLabel() {
        const selected = this.axisMeasuringRangeOptions.find(opt => opt.value === this.barGraph.displayType);
        return selected ? selected.translate : 'dlg.select';
    }

    getSelectedReferenceLabel() {
        const label = this.translate.instant('dlg.select');
        if (!this.barGraph?.divisions) {
            return label;
        }
        return this.barGraph.divisions;
    }

    onSelectReference(value: number) {
        this.barGraph.divisions = value;
    }

    toggleReferenceDropdown() {
        this.isReferenceDropdownOpen = !this.isReferenceDropdownOpen;
    }

    getInfoMessage(option: AxisMeasuringRange) {
        if (option === AxisMeasuringRange.Relative) {
            return this.translate.instant('graph.bar.table.axis-setting.range-format.relative.info');
        } else if (option === AxisMeasuringRange.Fixed) {
            return (
                this.translate.instant('graph.bar.table.axis-setting.range-format.fixed-value.info1') +
                '<br><br>' +
                this.translate.instant('graph.bar.table.axis-setting.range-format.fixed-value.info2')
            );
        }
    }

    optionsToggled(index: number): void {
        if (this.selectedRowIndex == index) {
            this.selectedRowIndex = null;
        } else {
            this.selectedRowIndex = index;
        }
    }
}
